import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { GestureResponderEvent, LayoutChangeEvent } from "react-native";
import { isWeb } from "tamagui";

import type { ButtonVariant } from "./types";

const brandGradientDefaultXPercent = 50;

const clampPercentage = (value: number) => Math.min(100, Math.max(0, value));

// Stable no-op handler for non-brand buttons.
const noop = () => {};

type UseBrandButtonGradientParams = {
  variant: ButtonVariant;
  isInteractionDisabled: boolean;
};

/**
 * Manages the cursor-tracking gradient overlay for brand buttons.
 * On web, positions the gradient via a CSS custom property (`--brand-gradient-x`);
 * on native, uses translateX via state. Returns no-ops for non-brand/disabled buttons.
 */
export const useBrandButtonGradient = ({
  variant,
  isInteractionDisabled,
}: UseBrandButtonGradientParams) => {
  const isBrandActive = variant === "brand" && !isInteractionDisabled;

  // State drives the `showBrandHoverGradient` return value (triggers re-renders).
  // Refs mirror state so that high-frequency callbacks (mousemove, touchmove) can
  // read the latest value without closing over stale state — this keeps those
  // callbacks stable (empty dep arrays) and avoids dropping the first event after
  // a state transition (e.g. mouseenter → mousemove in the same frame).
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const isHoveredRef = useRef(false);
  const isPressedRef = useRef(false);
  const [brandGradientXPercent, setBrandGradientXPercent] = useState(
    brandGradientDefaultXPercent,
  );
  const [brandGradientWidth, setBrandGradientWidth] = useState(0);
  const brandGradientTargetRef = useRef<HTMLElement | null>(null);
  const brandGradientXPercentRef = useRef(brandGradientDefaultXPercent);
  const brandGradientRafRef = useRef<number | null>(null);
  const brandGradientWidthRef = useRef(0);

  const resetBrandGradientPosition = useCallback((target?: HTMLElement) => {
    const resolvedTarget = target ?? brandGradientTargetRef.current;
    if (!isWeb) {
      brandGradientXPercentRef.current = brandGradientDefaultXPercent;
      setBrandGradientXPercent(brandGradientDefaultXPercent);
      return;
    }

    if (!resolvedTarget) {
      return;
    }

    if (brandGradientRafRef.current !== null) {
      cancelAnimationFrame(brandGradientRafRef.current);
      brandGradientRafRef.current = null;
    }

    brandGradientTargetRef.current = resolvedTarget;
    brandGradientXPercentRef.current = brandGradientDefaultXPercent;
    resolvedTarget.style.setProperty(
      "--brand-gradient-x",
      `${brandGradientDefaultXPercent}%`,
    );
  }, []);

  // Batches gradient position updates via rAF to avoid layout thrashing.
  const scheduleBrandGradientPosition = useCallback(
    (xPercent: number, target?: HTMLElement) => {
      brandGradientXPercentRef.current = clampPercentage(xPercent);
      if (isWeb && target) {
        brandGradientTargetRef.current = target;
      }

      if (brandGradientRafRef.current !== null) {
        return;
      }

      brandGradientRafRef.current = requestAnimationFrame(() => {
        brandGradientRafRef.current = null;
        if (!isWeb) {
          setBrandGradientXPercent(brandGradientXPercentRef.current);
          return;
        }

        const activeTarget = brandGradientTargetRef.current;
        if (!activeTarget) {
          return;
        }

        activeTarget.style.setProperty(
          "--brand-gradient-x",
          `${brandGradientXPercentRef.current}%`,
        );
      });
    },
    [],
  );

  const updateNativeBrandGradientPosition = useCallback(
    (locationX: number) => {
      if (isWeb) {
        return;
      }

      const width = brandGradientWidthRef.current;
      if (width <= 0) {
        return;
      }

      const xPercent = (locationX / width) * 100;
      scheduleBrandGradientPosition(xPercent);
    },
    [scheduleBrandGradientPosition],
  );

  const getNativeLocationX = useCallback((event?: GestureResponderEvent) => {
    const locationX = event?.nativeEvent?.locationX;
    return typeof locationX === "number" ? locationX : null;
  }, []);

  useEffect(() => {
    return () => {
      if (brandGradientRafRef.current !== null) {
        cancelAnimationFrame(brandGradientRafRef.current);
        brandGradientRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isBrandActive) {
      isHoveredRef.current = false;
      isPressedRef.current = false;
      setIsHovered(false);
      setIsPressed(false);
      resetBrandGradientPosition();
    }
  }, [isBrandActive, resetBrandGradientPosition]);

  const handleBrandPressIn = useCallback((_event?: GestureResponderEvent) => {
    isPressedRef.current = true;
    setIsPressed(true);
  }, []);

  const handleBrandPressOut = useCallback(() => {
    isPressedRef.current = false;
    setIsPressed(false);
  }, []);

  const handleBrandMouseEnter = useCallback(
    (_event: ReactMouseEvent<HTMLElement>) => {
      isHoveredRef.current = true;
      setIsHovered(true);
    },
    [],
  );

  const handleBrandMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (!isWeb) {
        return;
      }
      if (!isHoveredRef.current && !isPressedRef.current) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      if (rect.width <= 0) {
        return;
      }

      const xPercent = ((event.clientX - rect.left) / rect.width) * 100;
      scheduleBrandGradientPosition(xPercent, event.currentTarget);
    },
    [scheduleBrandGradientPosition],
  );

  const handleBrandTouchMove = useCallback(
    (event?: GestureResponderEvent) => {
      if (!isPressedRef.current) {
        return;
      }

      const locationX = getNativeLocationX(event);
      if (locationX !== null) {
        updateNativeBrandGradientPosition(locationX);
      }
    },
    [getNativeLocationX, updateNativeBrandGradientPosition],
  );

  const handleBrandTouchStart = useCallback(
    (event?: GestureResponderEvent) => {
      if (isWeb) {
        return;
      }

      isPressedRef.current = true;
      setIsPressed(true);

      const locationX = getNativeLocationX(event);
      if (locationX !== null) {
        updateNativeBrandGradientPosition(locationX);
      }
    },
    [getNativeLocationX, updateNativeBrandGradientPosition],
  );

  const handleBrandTouchEnd = useCallback(() => {
    isPressedRef.current = false;
    setIsPressed(false);
  }, []);

  const handleBrandMouseLeave = useCallback(
    (_event: ReactMouseEvent<HTMLElement>) => {
      isHoveredRef.current = false;
      setIsHovered(false);
      // Keep the last x position so opacity can fade out in-place.
    },
    [],
  );

  const handleBrandLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    brandGradientWidthRef.current = width;
    setBrandGradientWidth(width);
  }, []);

  // Web uses a CSS variable for smooth transform updates without re-renders;
  // native falls back to state-driven translateX.
  const brandGradientStyle = isWeb
    ? {
        transform: "translateX(calc(var(--brand-gradient-x, 50%) - 50%))",
        willChange: "transform, opacity",
      }
    : {
        transform: [
          {
            translateX:
              ((brandGradientXPercent - brandGradientDefaultXPercent) / 100) *
              brandGradientWidth,
          },
        ],
      };

  if (!isBrandActive) {
    return {
      showBrandHoverGradient: false,
      brandGradientStyle: {},
      handleBrandLayout: noop,
      handleBrandMouseEnter: noop,
      handleBrandMouseLeave: noop,
      handleBrandMouseMove: noop,
      handleBrandPressIn: noop,
      handleBrandPressOut: noop,
      handleBrandTouchEnd: noop,
      handleBrandTouchMove: noop,
      handleBrandTouchStart: noop,
    };
  }

  return {
    showBrandHoverGradient: isHovered || isPressed,
    brandGradientStyle,
    handleBrandLayout,
    handleBrandMouseEnter,
    handleBrandMouseLeave,
    handleBrandMouseMove,
    handleBrandPressIn,
    handleBrandPressOut,
    handleBrandTouchEnd,
    handleBrandTouchMove,
    handleBrandTouchStart,
  };
};
